import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  Image as ImageIcon, Type, Plus, Trash2, Save, Download, Loader2, Sparkles,
  Heart, Smile, HandCoins, Compass, Tent, Cross, Sprout, Activity, Smartphone, Eye, EyeOff,
} from 'lucide-react';

const CATEGORIES = [
  { id: 'health', label: 'Health', icon: Activity, color: '#0F7A4A' },
  { id: 'peace', label: 'Peace', icon: Smile, color: '#6B8E9E' },
  { id: 'relationships', label: 'Relationships', icon: Heart, color: '#B42318' },
  { id: 'financial', label: 'Financial Freedom', icon: HandCoins, color: '#D4A437' },
  { id: 'purpose', label: 'Purpose', icon: Compass, color: '#8A4FFF' },
  { id: 'adventure', label: 'Adventure', icon: Tent, color: '#E07A2C' },
  { id: 'faith', label: 'Faith', icon: Cross, color: '#3A6FB0' },
  { id: 'growth', label: 'Personal Growth', icon: Sprout, color: '#2F8F4F' },
];

// Royalty-free stock seeds (Unsplash search URLs as fallback inspiration). The builder
// also lets users paste any image URL.
const STARTER_IMAGES = [
  'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600',
  'https://images.unsplash.com/photo-1517960413843-0aee8e2b3285?w=600',
  'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=600',
  'https://images.unsplash.com/photo-1518609878373-06d740f60d8b?w=600',
  'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=600',
  'https://images.unsplash.com/photo-1502082553048-f009c37129b9?w=600',
];

const GUIDE_SECTIONS = [
  { title: 'Stop making fantasy boards.', body: 'A wall of yachts, mansions, and six-pack abs you cut from a magazine isn’t a vision board — it’s a wishlist. A real vision board is about who you’re BECOMING, not just what you’re collecting.' },
  { title: 'Build with intention, not aesthetic.', body: 'It doesn’t have to look pretty. It has to look TRUE. Pick images and words that hit you in the chest, not the ones that get the most likes.' },
  { title: 'Your environment shapes your mindset.', body: 'You become what you stare at every day. Make sure the version of you on this board is the version of you you’re actually trying to grow into.' },
  { title: 'Focus on purpose, not just stuff.', body: 'Yes — money, travel, freedom, all fair game. But anchor each one to WHY. "Financial freedom so I can be present with my kids." "Peace so I can stop white-knuckling life." The why makes it stick.' },
  { title: 'Daily visual reminders matter.', body: 'A vision board you check once a quarter is decoration. One you see every morning while you make coffee is a tool. Put it where your eyes go anyway.' },
];

const CANVAS_W = 1200;
const CANVAS_H = 800;

export default function VisionBoard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState('My Vision Board');
  const [background, setBackground] = useState('#0B0B0C');
  const [selected, setSelected] = useState(null);
  const [imgUrl, setImgUrl] = useState('');
  const [textInput, setTextInput] = useState('Live Life. Do Good. Be Happy.');
  const [activeCat, setActiveCat] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);

  // Load saved board
  useEffect(() => {
    if (!user) return;
    api.get('/vision-board').then(r => {
      setItems(r.data?.items || []);
      setTitle(r.data?.title || 'My Vision Board');
      setBackground(r.data?.background || '#0B0B0C');
    }).catch(() => {});
  }, [user]);

  const persist = useCallback(async (next = null) => {
    if (!user) return;
    setSaving(true);
    try {
      await api.put('/vision-board', { title, background, items: next || items });
      setSavedAt(Date.now());
    } finally { setSaving(false); }
  }, [user, title, background, items]);

  const updateItem = (id, patch) => setItems((prev) => prev.map((it) => it.id === id ? { ...it, ...patch } : it));
  const removeItem = (id) => { setItems(items.filter(it => it.id !== id)); setSelected(null); };

  const addImage = (src, opts = {}) => {
    const it = { id: `img-${Date.now()}`, type: 'image', src, x: 60 + Math.random() * 100, y: 60 + Math.random() * 100, w: 240, h: 240, rotation: 0, category: opts.category || null };
    setItems([...items, it]); setSelected(it.id);
  };
  const addText = (text) => {
    const it = { id: `txt-${Date.now()}`, type: 'text', text, x: 80 + Math.random() * 100, y: 80 + Math.random() * 100, w: 320, h: 80, rotation: 0, fontSize: 28, color: '#D4A437' };
    setItems([...items, it]); setSelected(it.id);
  };
  const addCategoryGoal = (cat) => {
    const it = { id: `goal-${Date.now()}`, type: 'goal', text: `Your ${cat.label.toUpperCase()} goal`, category: cat.id, x: 70 + Math.random() * 100, y: 70 + Math.random() * 100, w: 280, h: 120, rotation: 0, color: cat.color };
    setItems([...items, it]); setSelected(it.id);
  };

  // Pointer-based drag
  const startDrag = (e, id, mode = 'move') => {
    e.stopPropagation();
    if (e.target.dataset.role === 'handle') return; // handled below
    const it = items.find(i => i.id === id); if (!it) return;
    setSelected(id);
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = CANVAS_W / rect.width;
    const scaleY = CANVAS_H / rect.height;
    const startX = (e.clientX || e.touches?.[0]?.clientX) * scaleX;
    const startY = (e.clientY || e.touches?.[0]?.clientY) * scaleY;
    dragRef.current = { id, mode, startX, startY, origX: it.x, origY: it.y, origW: it.w, origH: it.h, scaleX, scaleY };
    window.addEventListener('pointermove', onDragMove);
    window.addEventListener('pointerup', endDrag, { once: true });
  };
  const onDragMove = (e) => {
    const d = dragRef.current; if (!d) return;
    const px = (e.clientX || e.touches?.[0]?.clientX) * d.scaleX;
    const py = (e.clientY || e.touches?.[0]?.clientY) * d.scaleY;
    const dx = px - d.startX;
    const dy = py - d.startY;
    if (d.mode === 'move') updateItem(d.id, { x: Math.max(0, Math.min(CANVAS_W - 20, d.origX + dx)), y: Math.max(0, Math.min(CANVAS_H - 20, d.origY + dy)) });
    else if (d.mode === 'resize') updateItem(d.id, { w: Math.max(60, d.origW + dx), h: Math.max(40, d.origH + dy) });
  };
  const endDrag = () => {
    dragRef.current = null;
    window.removeEventListener('pointermove', onDragMove);
    // Auto-save after drag end
    setTimeout(() => persist(), 200);
  };

  // PNG export — paint items onto a canvas
  const exportPng = async (asWallpaper = false) => {
    setExporting(true);
    try {
      const canvas = document.createElement('canvas');
      // Phone wallpaper = 9:19.5 portrait (iPhone-ish). Otherwise board ratio.
      const W = asWallpaper ? 1080 : CANVAS_W;
      const H = asWallpaper ? 2340 : CANVAS_H;
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext('2d');
      // Background
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, W, H);
      // Subtle gold gradient overlay
      const grad = ctx.createRadialGradient(W * 0.5, H * 0.4, 50, W * 0.5, H * 0.4, Math.max(W, H));
      grad.addColorStop(0, 'rgba(212,164,55,0.18)'); grad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);

      // Scale items if wallpaper (center & fit width)
      const scale = asWallpaper ? W / CANVAS_W : 1;
      const offsetY = asWallpaper ? (H - CANVAS_H * scale) / 2 : 0;

      // Preload images
      const imgEls = await Promise.all(items.filter(it => it.type === 'image').map(it => new Promise((resolve) => {
        const im = new Image(); im.crossOrigin = 'anonymous';
        im.onload = () => resolve({ it, im });
        im.onerror = () => resolve({ it, im: null });
        im.src = it.src;
      })));
      const imgMap = new Map(imgEls.map(e => [e.it.id, e.im]));

      // Paint items in order
      items.forEach((it) => {
        const x = it.x * scale; const y = it.y * scale + offsetY;
        const w = it.w * scale; const h = it.h * scale;
        ctx.save();
        ctx.translate(x + w / 2, y + h / 2);
        ctx.rotate(((it.rotation || 0) * Math.PI) / 180);
        ctx.translate(-w / 2, -h / 2);
        if (it.type === 'image') {
          const im = imgMap.get(it.id);
          if (im) {
            ctx.shadowColor = 'rgba(0,0,0,0.5)'; ctx.shadowBlur = 18; ctx.shadowOffsetY = 6;
            ctx.drawImage(im, 0, 0, w, h);
            ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;
          } else {
            ctx.fillStyle = '#222'; ctx.fillRect(0, 0, w, h);
          }
        } else if (it.type === 'text') {
          ctx.fillStyle = it.color || '#D4A437';
          ctx.font = `bold ${(it.fontSize || 28) * scale}px Bebas Neue, Inter, system-ui, sans-serif`;
          ctx.textBaseline = 'top';
          wrapText(ctx, it.text, 0, 0, w, (it.fontSize || 28) * scale * 1.05);
        } else if (it.type === 'goal') {
          // Rounded gold-bordered card with category color stripe
          ctx.fillStyle = 'rgba(255,255,255,0.06)';
          roundRect(ctx, 0, 0, w, h, 14, true, false);
          ctx.strokeStyle = it.color || '#D4A437';
          ctx.lineWidth = 3 * scale;
          roundRect(ctx, 1.5, 1.5, w - 3, h - 3, 13, false, true);
          ctx.fillStyle = it.color || '#D4A437';
          ctx.fillRect(0, 0, 6 * scale, h);
          ctx.fillStyle = '#FFFFFF';
          ctx.font = `bold ${22 * scale}px Bebas Neue, Inter, system-ui, sans-serif`;
          ctx.textBaseline = 'top';
          wrapText(ctx, it.text, 18 * scale, 16 * scale, w - 30 * scale, 26 * scale);
        }
        ctx.restore();
      });

      // Add small footer signature
      ctx.fillStyle = 'rgba(212,164,55,0.8)';
      ctx.font = `bold ${14 * scale}px Inter, system-ui, sans-serif`;
      ctx.textBaseline = 'bottom';
      ctx.fillText('KICKASSCOURSE.COM', 20 * scale, H - 20 * scale);

      const url = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url; a.download = asWallpaper ? `vision-board-wallpaper.png` : `vision-board.png`;
      document.body.appendChild(a); a.click(); a.remove();
    } finally { setExporting(false); }
  };

  const cat = activeCat ? CATEGORIES.find(c => c.id === activeCat) : null;

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative bg-ink text-white py-14 md:py-20 overflow-hidden" data-testid="vision-hero">
        <div className="absolute inset-0 pointer-events-none kc-noise opacity-50" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(700px 380px at 80% 50%, rgba(212,164,55,0.15), transparent 60%)' }} />
        <div className="relative kc-container">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[color:var(--kc-gold)]/40 bg-[color:var(--kc-gold)]/10 text-[10px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold"><Sparkles size={12} /> The Builder</div>
          <h1 className="mt-4 font-display uppercase leading-[0.9] max-w-4xl" style={{ fontSize: 'clamp(2.5rem, 6.5vw, 5.5rem)' }} data-testid="vision-title">
            BUILD A VISION BOARD<br /><span className="text-[color:var(--kc-gold)]">THAT ACTUALLY MEANS SOMETHING.</span>
          </h1>
          <p className="mt-4 text-lg text-white/80 max-w-2xl leading-relaxed">Not a fantasy collage. Not Pinterest porn. A real picture of who you’re becoming — built with intention, anchored in purpose, kept where your eyes go every day.</p>
        </div>
      </section>

      {/* GUIDE */}
      <section className="bg-white py-12 md:py-16 border-b border-kc" data-testid="vision-guide">
        <div className="kc-container grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {GUIDE_SECTIONS.map((g, i) => (
            <div key={i} className="bg-surface-2 rounded-[var(--kc-radius-md)] p-5 border border-kc">
              <div className="font-display text-3xl text-[color:var(--kc-gold)]">{String(i + 1).padStart(2, '0')}</div>
              <h3 className="mt-2 font-display uppercase text-xl leading-tight">{g.title}</h3>
              <p className="mt-2 text-sm text-ink-2 leading-relaxed">{g.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* BUILDER */}
      <section className="bg-surface-2 py-10 md:py-16">
        <div className="kc-container">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
            <div>
              <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold">Drag, drop, save, export</div>
              <h2 className="mt-2 font-display uppercase text-3xl md:text-4xl leading-tight">Your board</h2>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input value={title} onChange={(e) => setTitle(e.target.value)} className="h-10 px-3 rounded-[var(--kc-radius-sm)] border border-kc-strong bg-white text-sm" data-testid="vision-title-input" />
              <input type="color" value={background} onChange={(e) => setBackground(e.target.value)} className="h-10 w-12 rounded-[var(--kc-radius-sm)] border border-kc-strong bg-white cursor-pointer" title="Background color" data-testid="vision-bg" />
              <button onClick={() => setShowGrid(!showGrid)} className="h-10 px-3 rounded-[var(--kc-radius-sm)] border border-kc-strong bg-white text-sm font-semibold inline-flex items-center gap-1" data-testid="vision-grid-toggle">{showGrid ? <><Eye size={14}/> Grid</> : <><EyeOff size={14}/> Grid</>}</button>
              <button onClick={() => persist()} disabled={!user || saving} className="h-10 px-4 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] bg-ink text-white text-sm font-semibold disabled:opacity-50" data-testid="vision-save">{saving ? <><Loader2 size={14} className="animate-spin"/> Saving</> : <><Save size={14}/> Save</>}</button>
              <button onClick={() => exportPng(false)} disabled={exporting} className="h-10 px-4 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black text-sm font-semibold hover:bg-[color:var(--kc-gold-2)] disabled:opacity-50" data-testid="vision-export-png">{exporting ? <><Loader2 size={14} className="animate-spin"/> Exporting</> : <><Download size={14}/> PNG</>}</button>
              <button onClick={() => exportPng(true)} disabled={exporting} className="h-10 px-4 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] border border-[color:var(--kc-gold)] text-ink text-sm font-semibold hover:bg-gold-soft disabled:opacity-50" data-testid="vision-export-wp"><Smartphone size={14}/> Wallpaper</button>
            </div>
          </div>

          {!user && (
            <div className="mb-4 p-4 bg-white border border-kc rounded-[var(--kc-radius-md)] text-sm">
              <strong>You can play with the builder without an account</strong>, but to save and revisit your board, <Link to="/signup" className="text-[color:var(--kc-gold-2)] font-semibold underline">create a free account</Link> or <Link to="/login" className="font-semibold underline">log in</Link>.
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            {/* Toolbox */}
            <aside className="lg:col-span-3 space-y-4">
              {/* Categories */}
              <div className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-4">
                <div className="text-[10px] tracking-[0.22em] uppercase text-muted-kc font-semibold mb-2">Categories</div>
                <div className="grid grid-cols-2 gap-2">
                  {CATEGORIES.map((c) => {
                    const Icon = c.icon;
                    return (
                      <button key={c.id} onClick={() => { setActiveCat(c.id); addCategoryGoal(c); }} className="group p-2 rounded-[var(--kc-radius-sm)] border border-kc hover:border-[color:var(--kc-gold)] text-left transition-colors" data-testid={`vision-cat-${c.id}`}>
                        <Icon size={14} style={{ color: c.color }} />
                        <div className="mt-1 text-[11px] font-semibold leading-tight">{c.label}</div>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-[10px] text-muted-kc">Tap a category to drop a goal card on your board.</p>
              </div>

              {/* Add image */}
              <div className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-4">
                <div className="text-[10px] tracking-[0.22em] uppercase text-muted-kc font-semibold mb-2">Add image</div>
                <input value={imgUrl} onChange={(e) => setImgUrl(e.target.value)} placeholder="Paste image URL" className="kc-input-vb" data-testid="vision-img-url" />
                <button onClick={() => { if (imgUrl.trim()) { addImage(imgUrl.trim()); setImgUrl(''); } }} disabled={!imgUrl.trim()} className="mt-2 w-full h-9 inline-flex items-center justify-center gap-1 rounded-[var(--kc-radius-sm)] bg-ink text-white text-xs font-semibold disabled:opacity-40" data-testid="vision-img-add"><Plus size={12}/> Add image</button>
                <div className="mt-3 text-[10px] tracking-[0.22em] uppercase text-muted-kc font-semibold">Starters</div>
                <div className="mt-1 grid grid-cols-3 gap-1">
                  {STARTER_IMAGES.map((src) => (
                    <button key={src} onClick={() => addImage(src)} className="aspect-square rounded overflow-hidden border border-kc hover:border-[color:var(--kc-gold)]" data-testid="vision-starter">
                      <img src={src} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>

              {/* Add text */}
              <div className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-4">
                <div className="text-[10px] tracking-[0.22em] uppercase text-muted-kc font-semibold mb-2">Add text</div>
                <input value={textInput} onChange={(e) => setTextInput(e.target.value)} className="kc-input-vb" data-testid="vision-text-input" />
                <button onClick={() => { if (textInput.trim()) { addText(textInput.trim()); } }} className="mt-2 w-full h-9 inline-flex items-center justify-center gap-1 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black text-xs font-semibold" data-testid="vision-text-add"><Type size={12}/> Add text</button>
              </div>

              {/* Selected item controls */}
              {selected && (() => {
                const it = items.find(i => i.id === selected); if (!it) return null;
                return (
                  <div className="bg-white border border-[color:var(--kc-gold)] rounded-[var(--kc-radius-md)] p-4 space-y-2" data-testid="vision-selected">
                    <div className="text-[10px] tracking-[0.22em] uppercase text-muted-kc font-semibold">Selected</div>
                    <div className="text-xs">Type: <span className="font-bold uppercase">{it.type}</span></div>
                    {(it.type === 'text' || it.type === 'goal') && (
                      <textarea value={it.text} onChange={(e) => updateItem(it.id, { text: e.target.value })} rows={2} className="kc-input-vb" data-testid="vision-edit-text"/>
                    )}
                    {it.type === 'text' && (
                      <div className="flex items-center gap-2">
                        <label className="text-xs">Size</label>
                        <input type="range" min="14" max="80" value={it.fontSize || 28} onChange={(e) => updateItem(it.id, { fontSize: parseInt(e.target.value) })} className="flex-1"/>
                        <input type="color" value={it.color || '#D4A437'} onChange={(e) => updateItem(it.id, { color: e.target.value })} className="w-8 h-8"/>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <label className="text-xs">Rotate</label>
                      <input type="range" min="-45" max="45" value={it.rotation || 0} onChange={(e) => updateItem(it.id, { rotation: parseInt(e.target.value) })} className="flex-1"/>
                    </div>
                    <button onClick={() => removeItem(it.id)} className="w-full h-9 inline-flex items-center justify-center gap-1 rounded-[var(--kc-radius-sm)] bg-red-50 text-[color:var(--kc-danger)] text-xs font-semibold" data-testid="vision-delete"><Trash2 size={12}/> Delete item</button>
                  </div>
                );
              })()}
            </aside>

            {/* Canvas */}
            <div className="lg:col-span-9">
              <div className="relative rounded-[var(--kc-radius-md)] overflow-hidden border-2 border-kc-strong shadow-2xl" style={{ aspectRatio: `${CANVAS_W}/${CANVAS_H}`, background }} ref={canvasRef} onClick={() => setSelected(null)} data-testid="vision-canvas">
                {showGrid && (
                  <div className="absolute inset-0 pointer-events-none opacity-30" style={{ backgroundImage: 'linear-gradient(rgba(212,164,55,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(212,164,55,0.18) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
                )}
                {items.length === 0 && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white/60 pointer-events-none">
                    <ImageIcon size={36} className="mb-2 opacity-50" />
                    <div className="font-display uppercase text-2xl">Your board is empty.</div>
                    <div className="text-sm mt-1">Pick a category, paste an image URL, or add text from the left.</div>
                  </div>
                )}
                {items.map((it) => {
                  const styleBase = {
                    position: 'absolute',
                    left: `${(it.x / CANVAS_W) * 100}%`,
                    top: `${(it.y / CANVAS_H) * 100}%`,
                    width: `${(it.w / CANVAS_W) * 100}%`,
                    height: `${(it.h / CANVAS_H) * 100}%`,
                    transform: `rotate(${it.rotation || 0}deg)`,
                    cursor: 'move',
                  };
                  const isSel = selected === it.id;
                  if (it.type === 'image') {
                    return (
                      <div key={it.id} style={styleBase} onPointerDown={(e) => startDrag(e, it.id, 'move')} className={`group ${isSel ? 'ring-2 ring-[color:var(--kc-gold)]' : ''}`} data-testid={`vision-item-${it.id}`}>
                        <img src={it.src} alt="" className="w-full h-full object-cover rounded shadow-lg pointer-events-none" />
                        {isSel && <ResizeHandle onPointerDown={(e) => startDrag(e, it.id, 'resize')} />}
                      </div>
                    );
                  }
                  if (it.type === 'text') {
                    return (
                      <div key={it.id} style={{ ...styleBase, color: it.color, fontSize: it.fontSize || 28 }} onPointerDown={(e) => startDrag(e, it.id, 'move')} className={`font-display uppercase leading-[1.05] flex items-start ${isSel ? 'ring-2 ring-[color:var(--kc-gold)] bg-black/10' : ''}`} data-testid={`vision-item-${it.id}`}>
                        <span className="w-full text-shadow break-words">{it.text}</span>
                        {isSel && <ResizeHandle onPointerDown={(e) => startDrag(e, it.id, 'resize')} />}
                      </div>
                    );
                  }
                  // goal card
                  return (
                    <div key={it.id} style={{ ...styleBase, background: 'rgba(255,255,255,0.06)', border: `2px solid ${it.color || '#D4A437'}`, borderRadius: 12, backdropFilter: 'blur(6px)' }} onPointerDown={(e) => startDrag(e, it.id, 'move')} className={`overflow-hidden ${isSel ? 'ring-2 ring-[color:var(--kc-gold)]' : ''}`} data-testid={`vision-item-${it.id}`}>
                      <div style={{ width: 6, background: it.color || '#D4A437', height: '100%', position: 'absolute', left: 0, top: 0 }} />
                      <div className="p-3 pl-4 text-white font-display uppercase leading-tight" style={{ fontSize: 18 }}>{it.text}</div>
                      {isSel && <ResizeHandle onPointerDown={(e) => startDrag(e, it.id, 'resize')} />}
                    </div>
                  );
                })}
              </div>
              {savedAt && <div className="mt-2 text-xs text-muted-kc text-right">Saved {new Date(savedAt).toLocaleTimeString()}</div>}
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .kc-input-vb { width: 100%; padding: 0.5rem 0.6rem; border: 1px solid var(--kc-border-strong); border-radius: var(--kc-radius-sm); font-size: 0.85rem; outline: none; background: white; }
        .kc-input-vb:focus { border-color: var(--kc-gold); box-shadow: 0 0 0 2px var(--kc-gold-soft); }
        .text-shadow { text-shadow: 0 2px 12px rgba(0,0,0,0.5); }
      `}</style>
    </div>
  );
}

const ResizeHandle = ({ onPointerDown }) => (
  <span
    data-role="handle"
    onPointerDown={onPointerDown}
    className="absolute -right-1.5 -bottom-1.5 w-4 h-4 bg-[color:var(--kc-gold)] border-2 border-white rounded-sm cursor-se-resize"
    style={{ touchAction: 'none' }}
  />
);

// Canvas helpers
function roundRect(ctx, x, y, w, h, r, fill, stroke) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  if (fill) ctx.fill();
  if (stroke) ctx.stroke();
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = (text || '').split(/\s+/);
  let line = '';
  let yy = y;
  for (let n = 0; n < words.length; n++) {
    const testLine = line ? line + ' ' + words[n] : words[n];
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxWidth && line) {
      ctx.fillText(line, x, yy);
      line = words[n];
      yy += lineHeight;
    } else {
      line = testLine;
    }
  }
  if (line) ctx.fillText(line, x, yy);
}
